import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Globe } from '../ui/Globe';
import { Button } from '../ui/Button';
import { Icon, IconName } from '../ui/Icon';
import { useNavigation } from '../../hooks/useNavigation';

const SECTORS: { name: string; icon: IconName; color: string }[] = [
  { name: 'Energy', icon: 'zap', color: 'text-yellow-500' },
  { name: 'Automotive', icon: 'car', color: 'text-blue-500' },
  { name: 'Furniture', icon: 'home', color: 'text-amber-700' },
  { name: 'Camping', icon: 'mountain', color: 'text-green-600' },
  { name: 'FMCG', icon: 'shopping-cart', color: 'text-orange-500' },
  { name: 'Kitchen', icon: 'utensils-crossed', color: 'text-red-500' },
  { name: 'Coffee', icon: 'coffee', color: 'text-brown-500' },
];

export default function Hero() {
  const { goTo } = useNavigation();
  const containerRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section ref={containerRef} className="relative min-h-[110vh] flex flex-col items-center justify-center overflow-hidden bg-white">

      {/* Background Mesh Gradient */}
      <div className="absolute inset-0 w-full h-full opacity-30 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[70vw] h-[70vw] bg-orange-400/20 rounded-full blur-[100px] animate-blob" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[70vw] h-[70vw] bg-orange-300/20 rounded-full blur-[100px] animate-blob animation-delay-2000" />
        <div className="absolute top-[30%] left-[30%] w-[40vw] h-[40vw] bg-yellow-200/20 rounded-full blur-[100px] animate-blob animation-delay-4000" />
      </div>

      {/* Massive Globe Background */}
      <div className="absolute inset-0 z-0 flex items-center justify-center translate-y-[10%] md:translate-y-[20%] opacity-60 md:opacity-100 pointer-events-none md:pointer-events-auto">
        <Globe size={1200} className="max-w-[150vw] md:max-w-full" />
      </div>

      {/* Orbiting Sector Cards */}
      <div className="absolute inset-0 pointer-events-none hidden md:flex items-center justify-center translate-y-[10%] md:translate-y-[20%] z-20">
        <motion.div
          className="relative w-[400px] h-[400px] md:w-[800px] md:h-[800px] lg:w-[1000px] lg:h-[1000px]"
          animate={{ rotate: 360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
        >
          {SECTORS.map((sector, i) => {
            return (
              <div
                key={sector.name}
                className="absolute top-0 left-1/2 -ml-[2px] h-full" // Line through center vertically
                style={{ transform: `rotate(${(i / SECTORS.length) * 360}deg)` }}
              >
                {/* The Card at the top of the orbit (radius distance) */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
                    className="bg-white/80 backdrop-blur-md border border-white/50 shadow-lg px-4 py-3 rounded-2xl flex items-center gap-3 min-w-max"
                  >
                    <div className={`p-2 rounded-full bg-gray-50 ${sector.color}`}>
                      <Icon name={sector.icon} className="w-5 h-5" />
                    </div>
                    <span className="font-semibold text-gray-800 text-sm hidden md:block">{sector.name}</span>
                  </motion.div>
                </div>
              </div>
            )
          })}
        </motion.div>
      </div>

      {/* Content */}
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 text-center px-4 max-w-5xl mx-auto mt-[-10vh]"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-orange-200/50 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
          <span className="text-sm font-medium text-gray-600">Reimagining Global Logistics</span>
        </motion.div>

        <h1 className="text-6xl md:text-8xl font-bold tracking-tight text-gray-900 mb-6 leading-[0.9]">
          Future of <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
            Global Trade
          </span>
        </h1>

        <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10">
          The next generation of international trading. Seamless logistics, premium sourcing, and
          limitless possibilities—all powered by <span className="font-semibold text-gray-900">EasyBuy</span>.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            variant="primary"
            size="lg"
            className="rounded-full bg-black text-white hover:bg-gray-900 px-12 h-14 text-lg shadow-xl shadow-orange-500/20 min-w-[300px]"
            onClick={() => goTo('/contact')}
            rightIcon={<Icon name="arrow-right" className="w-5 h-5" />}
          >
            Contact Us
          </Button>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <div className="w-[1px] h-16 bg-gradient-to-b from-orange-500/0 via-orange-500 to-orange-500/0" />
      </motion.div>
    </section>
  );
}
